import { useEffect, useRef, useState } from 'react';

interface RevealOnScrollProps {
  children: React.ReactNode;
  delay?: number; // Delay in 100ms increments (1 = 100ms, 2 = 200ms, etc.)
  className?: string;
  direction?: 'up' | 'down' | 'left' | 'right';
  distance?: number; // Distance in pixels
}

/**
 * RevealOnScroll - Animate elements as they enter the viewport
 *
 * Usage:
 * <RevealOnScroll delay={1}>
 *   <YourComponent />
 * </RevealOnScroll>
 *
 * Props:
 * - delay: Delay before animation starts (in 100ms increments)
 * - direction: Direction of slide-in animation (default: 'up')
 * - distance: Distance to slide from (default: 24px)
 * - className: Additional CSS classes
 */
const RevealOnScroll = ({
  children,
  delay = 0,
  className = '',
  direction = 'up',
  distance = 24,
}: RevealOnScrollProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay * 100);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  // Calculate transform based on direction
  const getTransform = (visible: boolean): string => {
    if (visible) return 'translate(0, 0)';

    switch (direction) {
      case 'up':
        return `translateY(${distance}px)`;
      case 'down':
        return `translateY(-${distance}px)`;
      case 'left':
        return `translateX(${distance}px)`;
      case 'right':
        return `translateX(-${distance}px)`;
      default:
        return `translateY(${distance}px)`;
    }
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(isVisible),
      }}
    >
      {children}
    </div>
  );
};

export default RevealOnScroll;
